import React, { useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listInvoicesBetween } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Direct port of digicampusbd's `general-accounts-auto-fee-collection`
 * ("Ledger Wise Paid List") — despite living under the "General Accounts"
 * menu, confirmed via `GeneralAccountReportService.ledgerWiseIncomeStatement`
 * directly that this is 100% fee-invoice domain data (no `account_transaction`
 * join at all) — real paid invoices for ONE chosen date, pivoted ledger ×
 * fee-head. Built entirely client-side from the already-exposed `GET
 * /api/fee-invoices/range` (called with fromDate=toDate=the chosen date) —
 * no new backend needed.
 */
export default function LedgerWisePaidListPage() {
  const [date, setDate] = useState(today());
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setInvoices(await listInvoicesBetween(date, date));
    } catch (err) {
      setError("Failed to load ledger-wise paid list.");
    } finally {
      setLoading(false);
    }
  };

  const { feeHeadNames, rows } = useMemo(() => {
    if (!invoices) return { feeHeadNames: [], rows: [] };
    const headSet = new Set();
    const byLedger = new Map();
    invoices.forEach((inv) => {
      const key = inv.ledgerName || "(No Ledger)";
      if (!byLedger.has(key)) byLedger.set(key, {});
      const bucket = byLedger.get(key);
      (inv.lines || []).forEach((line) => {
        headSet.add(line.feeHeadName);
        bucket[line.feeHeadName] = (bucket[line.feeHeadName] || 0) + (line.paidAmount || 0);
      });
    });
    const names = Array.from(headSet).sort();
    const rowList = Array.from(byLedger.entries()).map(([ledgerName, details]) => ({
      ledgerName,
      details,
      total: names.reduce((s, h) => s + (details[h] || 0), 0),
    }));
    return { feeHeadNames: names, rows: rowList };
  }, [invoices]);

  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Ledger Wise Paid List</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Collection Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        invoices && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Ledger</th>
                  {feeHeadNames.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.ledgerName}>
                    <td>{r.ledgerName}</td>
                    {feeHeadNames.map((h) => (
                      <td key={h}>{(r.details[h] || 0).toFixed(2)}</td>
                    ))}
                    <td>{r.total.toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={feeHeadNames.length + 2}>No paid invoices found for this date.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 800 }} colSpan={feeHeadNames.length + 1}>
                    Grand Total
                  </td>
                  <td>
                    <strong>{grandTotal.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
